import { forwardRef } from 'react';
import type { ReactNode, SelectHTMLAttributes } from 'react';
import FormField from './FormField';
import Select from './Select';

type TimeOption = string | { value: string; label: string };

type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange' | 'children'> & {
  label?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: readonly TimeOption[];
  placeholder?: string;
  getOptionLabel?: (value: string) => string;
};

const TimePicker = forwardRef<HTMLSelectElement, Props>(
  (
    {
      label,
      hint,
      error,
      required,
      id,
      name,
      value,
      onChange,
      options,
      placeholder,
      getOptionLabel,
      ...props
    },
    ref
  ) => {
    return (
      <FormField label={label} htmlFor={id} hint={hint} error={error} required={required}>
        <Select
          ref={ref}
          id={id}
          name={name}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          error={Boolean(error)}
          {...props}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}

          {options.map((option) => {
            const value = typeof option === 'string' ? option : option.value;
            const label =
              typeof option === 'string'
                ? getOptionLabel
                  ? getOptionLabel(option)
                  : option
                : option.label;

            return (
              <option key={value} value={value}>
                {label}
              </option>
            );
          })}
        </Select>
      </FormField>
    );
  }
);

TimePicker.displayName = 'TimePicker';

export default TimePicker;
